import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { PRODUCTS_API_ADDRESS } from './apiConfig';
import './hierarchy.css';

const CATEGORIES_CACHE_KEY = 'nuvolem.products.categories';
const CATEGORIES_TTL_MS = 4 * 60 * 60 * 1000; // 4h — matches the server-side category cache

// Categories come back as an id->name object map, e.g. { "1": "Revit Tools" }.
// Normalise to a sorted array of { id, name } for rendering.
function normalizeCategories(map) {
  return Object.entries(map || {})
    .map(([id, name]) => ({ id: Number(id), name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

function readCachedCategories() {
  try {
    const raw = localStorage.getItem(CATEGORIES_CACHE_KEY);
    if (!raw) return null;
    const { data, fetchedAt } = JSON.parse(raw);
    if (!data || typeof fetchedAt !== 'number') return null;
    if (Date.now() - fetchedAt > CATEGORIES_TTL_MS) return null;
    return normalizeCategories(data);
  } catch {
    return null;
  }
}

function writeCachedCategories(map) {
  try {
    localStorage.setItem(
      CATEGORIES_CACHE_KEY,
      JSON.stringify({ data: map, fetchedAt: Date.now() })
    );
  } catch {
    // localStorage may be unavailable (private mode / quota) — caching is best-effort.
  }
}

async function fetchJson(url, signal) {
  const res = await fetch(url, { signal });
  if (!res.ok) throw new Error(`Request failed (${res.status})`);
  // The Function double-encodes the body as a JSON string, so a first parse may
  // yield a string that still needs parsing.
  const payload = await res.json();
  return typeof payload === 'string' ? JSON.parse(payload) : payload;
}

function ProductCard({ product }) {
  return (
    <Link className="item-card-link" to={`/products/${product.Id}`}>
      <article className="item-card">
        {product.ImageUrl ? (
          <img className="item-image" src={product.ImageUrl} alt={product.Name} loading="lazy" />
        ) : null}
        <h3 className="item-title">{product.Name}</h3>
        {product.Description ? (
          <p className="item-text">{product.Description}</p>
        ) : null}
      </article>
    </Link>
  );
}

function CategorySection({ category }) {
  // status: { loading, error, products }
  const [status, setStatus] = useState({ loading: true, error: null, products: [] });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    setStatus({ loading: true, error: null, products: [] });
    const url =
      `${PRODUCTS_API_ADDRESS}?operation=get_products` +
      `&category=${encodeURIComponent(category.id)}`;
    fetchJson(url, controller.signal)
      .then(data => {
        if (cancelled) return;
        setStatus({ loading: false, error: null, products: Array.isArray(data) ? data : [] });
      })
      .catch(error => {
        if (cancelled || error.name === 'AbortError') return;
        setStatus({ loading: false, error: 'Could not load products for this category.', products: [] });
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, [category.id]);

  return (
    <section className="item-group">
      <h2 className="item-group-title">{category.name}</h2>
      {status.loading ? (
        <p className="loading-text">Loading products…</p>
      ) : status.error ? (
        <p className="error-text">{status.error}</p>
      ) : status.products.length === 0 ? (
        <p className="empty-text">No products in this category yet.</p>
      ) : (
        <div className="item-grid">
          {status.products.map(product => (
            <ProductCard key={product.Id} product={product} />
          ))}
        </div>
      )}
    </section>
  );
}

function ProductsPage() {
  // categories: null = loading, [] = none, [...] = loaded; error is separate
  const [categories, setCategories] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const cached = readCachedCategories();
    if (cached) {
      setCategories(cached);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    fetchJson(`${PRODUCTS_API_ADDRESS}?operation=get_categories`, controller.signal)
      .then(map => {
        if (cancelled) return;
        writeCachedCategories(map);
        setCategories(normalizeCategories(map));
      })
      .catch(err => {
        if (cancelled || err.name === 'AbortError') return;
        setError('Could not load product categories. Please try again later.');
        setCategories([]);
      });
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  return (
    <div className="page">
      <h1 className="page-title">Products</h1>

      {categories === null ? (
        <p className="loading-text">Loading categories…</p>
      ) : error ? (
        <p className="error-text">{error}</p>
      ) : categories.length === 0 ? (
        <p className="empty-text">No products are available right now. Please check back soon.</p>
      ) : (
        categories.map(category => <CategorySection key={category.id} category={category} />)
      )}
    </div>
  );
}

export default ProductsPage;
